import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, CartesianGrid, XAxis, YAxis } from "recharts";
import { ShoppingBag, Plus, TrendingUp, Package, AlertTriangle, BarChart3, Truck, Edit, Trash2, Wallet, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["meals", "drinks", "juices", "fruits", "sweets", "snacks", "bakery"];

export default function CanteenAdminPage() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["dashboard", "products", "suppliers", "reports", "topups", "cashflow", "settings"];
  const [tab, setTab] = useState(validTabs.includes(tabFromUrl) ? tabFromUrl : "dashboard");

  // Sync URL ↔ state so sidebar links highlight correctly
  useEffect(() => {
    if (validTabs.includes(tabFromUrl) && tabFromUrl !== tab) setTab(tabFromUrl);
    // eslint-disable-next-line
  }, [tabFromUrl]);

  const onTabChange = (next) => {
    setTab(next);
    if (next === "dashboard") setSearchParams({});
    else setSearchParams({ tab: next });
  };

  return (
    <div className="space-y-4 max-w-7xl mx-auto" data-testid="canteen-admin-page">
      <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
        <ShoppingBag className="w-7 h-7 text-primary" /> {t("canteen_admin_title")}
      </h1>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="w-4 h-4 me-1" /> {t("dashboard")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products"><Package className="w-4 h-4 me-1" /> {t("products")}</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers"><Truck className="w-4 h-4 me-1" /> {t("suppliers")}</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><TrendingUp className="w-4 h-4 me-1" /> {t("reports")}</TabsTrigger>
          <TabsTrigger value="topups" data-testid="tab-topups"><Wallet className="w-4 h-4 me-1" /> {t("parent_topups")}</TabsTrigger>
          <TabsTrigger value="cashflow" data-testid="tab-cashflow"><CalendarIcon className="w-4 h-4 me-1" /> {t("cash_flow")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">{t("settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4"><Dashboard /></TabsContent>
        <TabsContent value="products" className="mt-4"><Products /></TabsContent>
        <TabsContent value="suppliers" className="mt-4"><Suppliers /></TabsContent>
        <TabsContent value="reports" className="mt-4"><Reports /></TabsContent>
        <TabsContent value="topups" className="mt-4"><TopupsReport /></TabsContent>
        <TabsContent value="cashflow" className="mt-4"><CashFlowCalendar /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function Dashboard() {
  const { t } = useI18n();
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [cashup, setCashup] = useState({ total: 0, orders: 0, items: 0 });
  const [low, setLow] = useState([]);
  const [top, setTop] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data || []);
      if ((r.data || []).length) {
        const demo = (r.data || []).find((s) => s.id === "demo-school-1");
        setSchoolId(demo ? demo.id : r.data[0].id);
      }
    });
  }, []);

  const refreshAlerts = () => {
    if (!schoolId) return;
    api.get(`/canteen/alerts?school_id=${schoolId}&only_unread=true`)
      .then((r) => setAlerts(r.data || []))
      .catch(() => setAlerts([]));
  };

  useEffect(() => {
    if (!schoolId) return;
    api.get(`/canteen/reports/daily-cashup?school_id=${schoolId}`).then((r) => setCashup(r.data));
    api.get(`/canteen/inventory/low-stock?school_id=${schoolId}`).then((r) => setLow(r.data || []));
    api.get(`/canteen/reports/top-products?school_id=${schoolId}&limit=5`).then((r) => setTop(r.data || []));
    refreshAlerts();
  }, [schoolId]); // eslint-disable-line

  const markAllRead = async () => {
    if (!schoolId) return;
    await api.post(`/canteen/alerts/mark-all-read?school_id=${schoolId}`);
    refreshAlerts();
  };

  const markOneRead = async (id) => {
    await api.patch(`/canteen/alerts/${id}/read`);
    refreshAlerts();
  };

  return (
    <div className="space-y-4">
      <SchoolSelector value={schoolId} onChange={setSchoolId} schools={schools} />

      {alerts.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800" data-testid="canteen-alerts-panel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4" /> {t("canteen_alerts")} ({alerts.length})
            </h3>
            <Button size="sm" variant="ghost" onClick={markAllRead} data-testid="alerts-mark-all-read">
              {t("mark_all_read")}
            </Button>
          </div>
          <ul className="space-y-1.5">
            {alerts.slice(0, 6).map((a) => (
              <li key={a.id} className="flex items-start gap-2 text-sm" data-testid={`alert-${a.id}`}>
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${a.severity === "warning" ? "bg-amber-500" : a.severity === "success" ? "bg-emerald-500" : "bg-blue-500"}`} />
                <span className="flex-1">{a.title}</span>
                <button onClick={() => markOneRead(a.id)} className="text-xs text-muted-foreground hover:text-foreground">
                  {t("dismiss")}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat icon={ShoppingBag} label={t("today_sales")} value={`${cashup.total.toFixed(2)} ${t("sar_unit")}`} color="primary" />
        <Stat icon={Package} label={t("today_orders")} value={cashup.orders} color="emerald" />
        <Stat icon={Truck} label={t("items_sold")} value={cashup.items} color="blue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border shadow-none">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> {t("top_products")}</h3>
          {top.length === 0 ? <div className="text-xs text-muted-foreground py-6 text-center">{t("no_data_yet")}</div> :
            top.map((p) => (
              <div key={p.product_id} className="flex justify-between py-2 border-b border-border text-sm">
                <span>{p.name}</span>
                <span className="font-mono text-muted-foreground">× {p.qty} · {p.revenue.toFixed(2)}</span>
              </div>
            ))}
        </Card>
        <Card className="p-5 border shadow-none">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> {t("low_stock_alert")}</h3>
          {low.length === 0 ? <div className="text-xs text-muted-foreground py-6 text-center">{t("all_well_stocked")}</div> :
            low.map((p) => (
              <div key={p.id} className="flex justify-between py-2 border-b border-border text-sm" data-testid={`low-${p.id}`}>
                <span>{p.name_ar}</span>
                <span className="font-mono text-amber-600">{p.stock_qty} / {p.low_stock_threshold || 5}</span>
              </div>
            ))}
        </Card>
      </div>
    </div>
  );
}

function Products() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name_ar: "", name_en: "", category: "meals", price: "", stock_qty: 0, low_stock_threshold: 5, is_healthy: false, active: true });

  const load = async () => {
    if (!schoolId) return;
    const { data } = await api.get(`/canteen/products?school_id=${schoolId}`);
    setItems(data || []);
  };
  useEffect(() => {
    api.get("/schools").then((r) => { setSchools(r.data || []); if ((r.data || []).length) setSchoolId(r.data[0].id); });
  }, []);
  useEffect(() => { load(); }, [schoolId]);

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), stock_qty: parseInt(form.stock_qty) || 0,
                        low_stock_threshold: parseInt(form.low_stock_threshold) || 5, school_id: schoolId };
      if (editing) {
        delete payload.school_id;
        await api.put(`/canteen/products/${editing.id}`, payload);
      } else {
        await api.post("/canteen/products", payload);
      }
      toast.success(t("saved")); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name_ar: p.name_ar, name_en: p.name_en || "", category: p.category, price: p.price,
      stock_qty: p.stock_qty, low_stock_threshold: p.low_stock_threshold || 5,
      is_healthy: p.is_healthy, active: p.active,
    });
    setOpen(true);
  };

  const startNew = () => {
    setEditing(null);
    setForm({ name_ar: "", name_en: "", category: "meals", price: "", stock_qty: 0, low_stock_threshold: 5, is_healthy: false, active: true });
    setOpen(true);
  };

  const remove = async (p) => {
    if (!window.confirm(`${t("confirm_delete")} ${p.name_ar}?`)) return;
    await api.delete(`/canteen/products/${p.id}`);
    toast.success(t("done_ok")); load();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-2">
        <SchoolSelector value={schoolId} onChange={setSchoolId} schools={schools} />
        <Button onClick={startNew} data-testid="add-product"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>
      </div>
      <Card className="p-0 border shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr><th className="px-3 py-2 text-start">{t("name_ar")}</th>
                <th className="px-3 py-2 text-start">{t("category")}</th>
                <th className="px-3 py-2 text-start">{t("price")}</th>
                <th className="px-3 py-2 text-start">{t("stock")}</th>
                <th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">{t("no_data_yet")}</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className={`border-t border-border ${!p.active ? "opacity-50" : ""}`} data-testid={`prod-row-${p.id}`}>
                <td className="px-3 py-2">{p.name_ar} {p.is_healthy && <span className="text-emerald-600 text-xs ms-1">✓</span>}</td>
                <td className="px-3 py-2 text-xs">{t(`cat_${p.category}`)}</td>
                <td className="px-3 py-2 font-mono">{p.price}</td>
                <td className={`px-3 py-2 font-mono ${p.stock_qty <= (p.low_stock_threshold || 5) ? "text-amber-600" : ""}`}>{p.stock_qty}</td>
                <td className="px-3 py-2 text-end">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(p)}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_product")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("name_ar")}</Label><Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="pf-name-ar" /></div>
              <div><Label>{t("name_en")}</Label><Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="pf-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("price")} ({t("sar_unit")})</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("stock")}</Label><Input type="number" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} /></div>
              <div><Label>{t("low_stock_threshold")}</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_healthy} onChange={(e) => setForm({ ...form, is_healthy: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> <span className="text-sm">{t("is_healthy")}</span></label>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button className="flex-1" onClick={save} data-testid="pf-save">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Suppliers() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "" });
  const load = async () => { const { data } = await api.get("/canteen/suppliers"); setItems(data || []); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/canteen/suppliers", form); toast.success(t("saved")); setOpen(false); setForm({ name: "", contact_person: "", phone: "", email: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`${t("confirm_delete")} ${s.name}?`)) return;
    await api.delete(`/canteen/suppliers/${s.id}`); load();
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)}><Plus className="w-4 h-4 me-1" /> {t("new_supplier")}</Button></div>
      <Card className="p-0 border shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr><th className="px-3 py-2 text-start">{t("name")}</th><th className="px-3 py-2 text-start">{t("contact")}</th><th className="px-3 py-2 text-start">{t("phone")}</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">{t("no_data_yet")}</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-3 py-2">{s.name}</td>
                <td className="px-3 py-2">{s.contact_person || "—"}</td>
                <td className="px-3 py-2 font-mono">{s.phone || "—"}</td>
                <td className="px-3 py-2 text-end"><Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(s)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("new_supplier")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>{t("contact")}</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <Button onClick={save} className="w-full">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <PurchaseOrders suppliers={items} />
    </div>
  );
}

function PurchaseOrders({ suppliers }) {
  const { t } = useI18n();
  const [pos, setPos] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ product_id: "", qty: 1, unit_cost: 0 }]);

  const load = async () => {
    const { data } = await api.get("/canteen/purchase-orders");
    setPos(data || []);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/canteen/products").then((r) => setProducts(r.data || []));
  }, []);

  const addLine = () => setLines([...lines, { product_id: "", qty: 1, unit_cost: 0 }]);
  const updateLine = (idx, patch) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);

  const save = async () => {
    if (!supplierId) { toast.error(t("select_supplier")); return; }
    const items = lines.filter((l) => l.product_id && l.qty > 0);
    if (items.length === 0) { toast.error(t("add_items")); return; }
    try {
      await api.post("/canteen/purchase-orders", { supplier_id: supplierId, items, notes });
      toast.success(t("saved"));
      setOpen(false); setSupplierId(""); setNotes("");
      setLines([{ product_id: "", qty: 1, unit_cost: 0 }]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const receive = async (po) => {
    if (!window.confirm(`${t("confirm_receive_po")} (${po.total.toFixed(2)} ${t("sar_unit")})?`)) return;
    try { await api.post(`/canteen/purchase-orders/${po.id}/receive`); toast.success(t("po_received")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cancel = async (po) => {
    if (!window.confirm(`${t("confirm_cancel")} ${po.supplier_name}?`)) return;
    try { await api.post(`/canteen/purchase-orders/${po.id}/cancel`); toast.success(t("saved")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const statusBadge = (s) => {
    const map = { pending: "bg-amber-100 text-amber-700", received: "bg-emerald-100 text-emerald-700", cancelled: "bg-rose-100 text-rose-600" };
    return <span className={`px-2 py-0.5 rounded text-xs ${map[s] || "bg-muted"}`}>{t(`po_status_${s}`)}</span>;
  };

  return (
    <Card className="p-4 border shadow-none" data-testid="canteen-purchase-orders">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /> {t("purchase_orders")}</h3>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-po-btn"><Plus className="w-4 h-4 me-1" /> {t("new_purchase_order")}</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-start">{t("supplier")}</th>
              <th className="px-3 py-2 text-start">{t("items")}</th>
              <th className="px-3 py-2 text-start">{t("total")}</th>
              <th className="px-3 py-2 text-start">{t("status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">{t("no_data_yet")}</td></tr>}
            {pos.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`po-${p.id}`}>
                <td className="px-3 py-2">{p.supplier_name}</td>
                <td className="px-3 py-2">{p.items.length}</td>
                <td className="px-3 py-2 font-semibold">{p.total.toFixed(2)} {t("sar_unit")}</td>
                <td className="px-3 py-2">{statusBadge(p.status)}</td>
                <td className="px-3 py-2 text-end space-x-1">
                  {p.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => receive(p)} data-testid={`receive-po-${p.id}`}>{t("receive")}</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(p)}>{t("cancel")}</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("new_purchase_order")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("supplier")}</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder={t("select_supplier")} /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("items")}</Label>
              {lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <Select value={l.product_id} onValueChange={(v) => updateLine(idx, { product_id: v })}>
                      <SelectTrigger><SelectValue placeholder={t("select_product")} /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name_ar || p.name_en}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" min="1" value={l.qty} onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })} placeholder={t("qty")} /></div>
                  <div className="col-span-3"><Input type="number" step="0.5" min="0" value={l.unit_cost} onChange={(e) => updateLine(idx, { unit_cost: Number(e.target.value) })} placeholder={t("unit_cost")} /></div>
                  <div className="col-span-1">
                    {lines.length > 1 && (
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => removeLine(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addLine} className="w-full" data-testid="po-add-line"><Plus className="w-4 h-4 me-1" /> {t("add_item")}</Button>
            </div>
            <div><Label>{t("notes")}</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm text-muted-foreground">{t("total")}</span>
              <span className="text-xl font-bold">{total.toFixed(2)} {t("sar_unit")}</span>
            </div>
            <Button onClick={save} className="w-full" data-testid="po-save">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Reports() {
  const { t } = useI18n();
  const [sales, setSales] = useState([]);
  const [top, setTop] = useState([]);
  const [days, setDays] = useState(30);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");

  useEffect(() => { api.get("/schools").then((r) => { setSchools(r.data || []); if ((r.data || []).length) setSchoolId(r.data[0].id); }); }, []);
  useEffect(() => {
    if (!schoolId) return;
    api.get(`/canteen/reports/sales?school_id=${schoolId}&days=${days}`).then((r) => setSales(r.data || []));
    api.get(`/canteen/reports/top-products?school_id=${schoolId}&days=${days}`).then((r) => setTop(r.data || []));
  }, [schoolId, days]);

  const total = sales.reduce((s, d) => s + d.total, 0);
  const maxDay = Math.max(...sales.map((d) => d.total), 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <SchoolSelector value={schoolId} onChange={setSchoolId} schools={schools} />
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t("last_7_days")}</SelectItem>
            <SelectItem value="30">{t("last_30_days")}</SelectItem>
            <SelectItem value="90">{t("last_90_days")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="ms-auto font-mono text-sm">{t("total")}: <b className="text-primary">{total.toFixed(2)} {t("sar_unit")}</b></div>
      </div>

      <Card className="p-5 border shadow-none">
        <h3 className="font-semibold mb-4">{t("daily_sales")}</h3>
        {sales.length === 0 ? <div className="text-center text-sm text-muted-foreground py-8">{t("no_data_yet")}</div> : (
          <div className="flex items-end gap-1 h-48">
            {sales.map((d) => (
              <div key={d.date} className="flex-1 group relative flex flex-col items-center justify-end" title={`${d.date}: ${d.total}`}>
                <div className="w-full bg-primary/80 rounded-t hover:bg-primary smooth" style={{ height: `${(d.total / maxDay) * 100}%`, minHeight: d.total > 0 ? "2px" : "0" }} />
                <div className="text-[8px] mt-1 text-muted-foreground rotate-45 origin-bottom-left">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 border shadow-none">
        <h3 className="font-semibold mb-3">{t("top_products")}</h3>
        {top.map((p, i) => (
          <div key={p.product_id} className="flex justify-between py-2 border-b border-border text-sm">
            <span><span className="font-mono text-muted-foreground me-2">#{i + 1}</span>{p.name}</span>
            <span className="font-mono">× {p.qty} · {p.revenue.toFixed(2)}</span>
          </div>
        ))}
        {top.length === 0 && <div className="text-center text-sm text-muted-foreground py-4">{t("no_data_yet")}</div>}
      </Card>

      <AdvancedCharts schoolId={schoolId} days={days} />
    </div>
  );
}

function AdvancedCharts({ schoolId, days }) {
  const { t } = useI18n();
  const [byCat, setByCat] = useState([]);
  const [healthy, setHealthy] = useState({ healthy_qty: 0, unhealthy_qty: 0, healthy_pct: 0 });
  const [hourly, setHourly] = useState([]);

  useEffect(() => {
    if (!schoolId) return;
    api.get(`/canteen/reports/by-category?school_id=${schoolId}&days=${days}`).then((r) => setByCat(r.data || []));
    api.get(`/canteen/reports/healthy-ratio?school_id=${schoolId}&days=${days}`).then((r) => setHealthy(r.data));
    api.get(`/canteen/reports/hourly?school_id=${schoolId}&days=${Math.min(days, 14)}`).then((r) => setHourly(r.data || []));
  }, [schoolId, days]);

  const PIE_COLORS = ["#16a34a", "#0ea5e9", "#f59e0b", "#ec4899", "#8b5cf6", "#ef4444", "#64748b"];
  const healthyData = [
    { name: t("healthy"), value: healthy.healthy_qty, fill: "#16a34a" },
    { name: t("unhealthy"), value: healthy.unhealthy_qty, fill: "#94a3b8" },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="canteen-advanced-charts">
      <Card className="p-5 border shadow-none">
        <h3 className="font-semibold mb-3">{t("sales_by_category")}</h3>
        {byCat.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{t("no_data_yet")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={byCat.map((c) => ({ name: t(`cat_${c.category}`) || c.category, value: c.total }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                {byCat.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => `${Number(v).toFixed(2)} ${t("sar_unit")}`} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-5 border shadow-none">
        <h3 className="font-semibold mb-3 flex items-center justify-between">
          <span>{t("healthy_ratio")}</span>
          <span className="text-emerald-700 dark:text-emerald-300 font-mono text-sm">{healthy.healthy_pct.toFixed(1)}%</span>
        </h3>
        {(healthy.healthy_qty + healthy.unhealthy_qty) === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{t("no_data_yet")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={healthyData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-5 border shadow-none md:col-span-2">
        <h3 className="font-semibold mb-3">{t("hourly_distribution")}</h3>
        {hourly.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{t("no_data_yet")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} />
              <YAxis />
              <Tooltip formatter={(v) => `${v}`} labelFormatter={(h) => `${h}:00`} />
              <Bar dataKey="orders" fill="#16a34a" name={t("today_orders")} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

function TopupsReport() {
  const { t } = useI18n();
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data || []);
      if ((r.data || []).length) {
        const demo = (r.data || []).find((s) => s.id === "demo-school-1");
        setSchoolId(demo ? demo.id : r.data[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    api.get(`/canteen/reports/parent-topups?school_id=${schoolId}&days=${days}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [schoolId, days]);

  const downloadCSV = () => {
    if (!data?.transactions?.length) return;
    const header = ["date", "parent_name", "student_name", "amount", "gateway", "status", "ref"];
    const rows = data.transactions.map((t) => [
      (t.at || "").slice(0, 19), t.parent_name || "", t.student_name || "",
      t.amount, t.gateway || "", t.status || "", t.gateway_ref || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `parent-topups-${days}d.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="topups-report">
      <div className="flex flex-wrap items-center gap-2">
        <SchoolSelector value={schoolId} onChange={setSchoolId} schools={schools} />
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-36" data-testid="topups-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t("last_7_days")}</SelectItem>
            <SelectItem value="30">{t("last_30_days")}</SelectItem>
            <SelectItem value="90">{t("last_90_days")}</SelectItem>
            <SelectItem value="365">{t("last_year")}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={downloadCSV} data-testid="topups-csv-download">{t("download_csv")}</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat icon={Wallet} label={t("total_topups")}
              value={`${(data?.total || 0).toFixed(2)} ${t("sar_unit")}`} color="primary" />
        <Stat icon={TrendingUp} label={t("transactions_count")} value={data?.count || 0} color="emerald" />
        <Stat icon={ShoppingBag} label={t("parents_count")} value={data?.by_parent?.length || 0} color="blue" />
      </div>

      <Card className="p-5 border shadow-none">
        <h3 className="font-semibold mb-3">{t("topups_by_parent")}</h3>
        {loading && <div className="text-center text-sm text-muted-foreground py-4">{t("loading")}…</div>}
        {!loading && (!data?.by_parent?.length) && <div className="text-center text-sm text-muted-foreground py-6">{t("no_data_yet")}</div>}
        {data?.by_parent?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-start">{t("parent")}</th>
                  <th className="px-3 py-2 text-start">{t("transactions_count")}</th>
                  <th className="px-3 py-2 text-end">{t("total")}</th>
                </tr>
              </thead>
              <tbody>
                {data.by_parent.map((p) => (
                  <tr key={p.parent_id} className="border-t border-border" data-testid={`topup-parent-${p.parent_id}`}>
                    <td className="px-3 py-2">{p.parent_name || p.parent_id}</td>
                    <td className="px-3 py-2 font-mono">{p.count}</td>
                    <td className="px-3 py-2 text-end font-mono font-semibold text-emerald-700 dark:text-emerald-300">
                      {p.total.toFixed(2)} {t("sar_unit")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data?.by_day?.length > 0 && (
        <Card className="p-5 border shadow-none">
          <h3 className="font-semibold mb-3">{t("topups_by_day")}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_day}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
              <YAxis />
              <Tooltip formatter={(v) => `${Number(v).toFixed(2)} ${t("sar_unit")}`} />
              <Bar dataKey="total" fill="#16a34a" name={t("topups")} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card className="p-5 border shadow-none">
        <h3 className="font-semibold mb-3">{t("transaction_history")} ({data?.transactions?.length || 0})</h3>
        {(!data?.transactions?.length) && <div className="text-center text-sm text-muted-foreground py-6">{t("no_data_yet")}</div>}
        {data?.transactions?.length > 0 && (
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-start">{t("date")}</th>
                  <th className="px-3 py-2 text-start">{t("parent")}</th>
                  <th className="px-3 py-2 text-start">{t("student")}</th>
                  <th className="px-3 py-2 text-start">{t("payment_method")}</th>
                  <th className="px-3 py-2 text-end">{t("amount")}</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((tx) => (
                  <tr key={tx.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{(tx.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2">{tx.parent_name || "—"}</td>
                    <td className="px-3 py-2">{tx.student_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{tx.gateway}</td>
                    <td className="px-3 py-2 text-end font-mono text-emerald-700 dark:text-emerald-300">
                      +{Number(tx.amount).toFixed(2)} {t("sar_unit")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function CashFlowCalendar() {
  const { t } = useI18n();
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth() + 1);
  const [monthData, setMonthData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(today.toISOString().slice(0, 10));
  const [dayData, setDayData] = useState(null);

  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data || []);
      if ((r.data || []).length) {
        const demo = (r.data || []).find((s) => s.id === "demo-school-1");
        setSchoolId(demo ? demo.id : r.data[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    api.get(`/canteen/reports/activity/month?school_id=${schoolId}&year=${year}&month=${month}`)
      .then((r) => setMonthData(r.data || []));
  }, [schoolId, year, month]);

  useEffect(() => {
    if (!schoolId || !selectedDay) return;
    api.get(`/canteen/reports/activity?school_id=${schoolId}&day=${selectedDay}`)
      .then((r) => setDayData(r.data))
      .catch(() => setDayData(null));
  }, [schoolId, selectedDay]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else { setMonth(month - 1); }
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else { setMonth(month + 1); }
  };

  // Build calendar grid
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  const startWeekday = firstDay.getUTCDay(); // 0=Sun..6=Sat
  const daysInMonth = monthData.length || new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(monthData[d - 1] || { date: null });

  const maxNet = Math.max(...monthData.map((d) => Math.abs(d.net || 0)), 1);
  const monthName = firstDay.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const eventColor = (kind) => {
    if (kind === "topup") return "bg-emerald-500";
    if (kind === "purchase") return "bg-blue-500";
    if (kind === "refund") return "bg-amber-500";
    if (kind === "adjustment") return "bg-violet-500";
    if (kind === "preorder_hold") return "bg-cyan-500";
    if (kind === "preorder_refund") return "bg-amber-400";
    if (kind === "po_received") return "bg-rose-500";
    return "bg-slate-400";
  };

  return (
    <div className="space-y-4" data-testid="cashflow-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <SchoolSelector value={schoolId} onChange={setSchoolId} schools={schools} />
        <div className="flex items-center gap-1 ms-auto">
          <Button variant="outline" size="sm" onClick={prevMonth} data-testid="cashflow-prev-month">‹</Button>
          <span className="text-sm font-semibold px-2 min-w-[140px] text-center">{monthName}</span>
          <Button variant="outline" size="sm" onClick={nextMonth} data-testid="cashflow-next-month">›</Button>
        </div>
      </div>

      <Card className="p-4 border shadow-none">
        <div className="grid grid-cols-7 gap-1 text-xs text-center text-muted-foreground mb-1">
          {[t("sun"), t("mon"), t("tue"), t("wed"), t("thu"), t("fri"), t("sat")].map((d, i) => (
            <div key={i} className="py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c?.date) return <div key={i} className="aspect-square" />;
            const isSelected = c.date === selectedDay;
            const intensity = Math.min(1, Math.abs(c.net) / maxNet);
            const positive = c.net >= 0;
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(c.date)}
                className={`aspect-square rounded-lg border text-xs flex flex-col items-center justify-center transition
                  ${isSelected ? "ring-2 ring-primary border-primary" : "border-border hover:border-primary/40"}
                  ${c.events > 0 ? (positive ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-rose-50 dark:bg-rose-950/30") : ""}
                `}
                style={c.events > 0 ? { backgroundColor: positive
                  ? `rgba(16,185,129,${0.05 + intensity * 0.25})`
                  : `rgba(244,63,94,${0.05 + intensity * 0.25})` } : {}}
                data-testid={`day-${c.date}`}
              >
                <span className="font-semibold">{Number(c.date.slice(-2))}</span>
                {c.events > 0 && (
                  <span className={`text-[10px] font-mono ${positive ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>
                    {positive ? "+" : ""}{c.net.toFixed(0)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/50" /> {t("net_positive")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-500/50" /> {t("net_negative")}</span>
        </div>
      </Card>

      {dayData && (
        <Card className="p-5 border shadow-none" data-testid="day-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">{t("day_activity")}: {selectedDay}</h3>
            <span className={`font-mono font-bold ${dayData.net_cash_in >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {dayData.net_cash_in >= 0 ? "+" : ""}{dayData.net_cash_in.toFixed(2)} {t("sar_unit")}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-sm">
            <SummaryPill label={t("topups")} value={`+${dayData.summary.topups.toFixed(2)}`} count={dayData.summary.topups_count} color="emerald" />
            <SummaryPill label={t("sales")} value={`+${dayData.summary.sales.toFixed(2)}`} count={dayData.summary.sales_count} color="blue" />
            <SummaryPill label={t("preorders")} value={`+${dayData.summary.preorders.toFixed(2)}`} count={dayData.summary.preorders_count} color="cyan" />
            <SummaryPill label={t("po_received")} value={`-${dayData.summary.po_received.toFixed(2)}`} count={dayData.summary.po_received_count} color="rose" />
          </div>

          <h4 className="font-semibold text-sm mb-2">{t("timeline")} ({dayData.events.length})</h4>
          {dayData.events.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-6">{t("no_activity_this_day")}</div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {dayData.events.map((e, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 rounded-lg border border-border hover:bg-muted/50" data-testid={`event-${idx}`}>
                  <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${eventColor(e.kind)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm truncate">{e.title}</div>
                      <div className={`text-sm font-mono shrink-0 ${e.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {e.amount >= 0 ? "+" : ""}{Number(e.amount).toFixed(2)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {(e.at || "").slice(11, 19)} · {t(`kind_${e.kind}`) !== `kind_${e.kind}` ? t(`kind_${e.kind}`) : e.kind}
                      {e.note && <span> · {e.note}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function SummaryPill({ label, value, count, color }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800",
    blue: "border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800",
    cyan: "border-cyan-200 bg-cyan-50 dark:bg-cyan-950/20 dark:border-cyan-800",
    rose: "border-rose-200 bg-rose-50 dark:bg-rose-950/20 dark:border-rose-800",
  };
  return (
    <div className={`border rounded-lg p-2 ${colors[color]}`}>
      <div className="text-xs text-muted-foreground">{label} ({count})</div>
      <div className="font-mono font-bold">{value}</div>
    </div>
  );
}

function SettingsTab() {
  const { t } = useI18n();
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [s, setS] = useState({ enabled: true, max_daily_spend_default: 30, healthy_policy: "recommend" });

  useEffect(() => { api.get("/schools").then((r) => { setSchools(r.data || []); if ((r.data || []).length) setSchoolId(r.data[0].id); }); }, []);
  useEffect(() => { if (schoolId) api.get(`/canteen/settings?school_id=${schoolId}`).then((r) => setS(r.data)); }, [schoolId]);

  const save = async () => {
    try {
      await api.put("/canteen/settings", { ...s, school_id: schoolId });
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    }
  };

  return (
    <div className="space-y-4 max-w-xl">
      <SchoolSelector value={schoolId} onChange={setSchoolId} schools={schools} />
      <Card className="p-5 border shadow-none space-y-4">
        <label className="flex items-center gap-2"><input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="canteen-enable" /> <span>{t("enable_canteen")}</span></label>
        <div><Label>{t("default_daily_limit")} ({t("sar_unit")})</Label><Input type="number" value={s.max_daily_spend_default} onChange={(e) => setS({ ...s, max_daily_spend_default: parseFloat(e.target.value) })} data-testid="canteen-daily-limit" /></div>
        <div>
          <Label>{t("healthy_policy")}</Label>
          <Select value={s.healthy_policy} onValueChange={(v) => setS({ ...s, healthy_policy: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("policy_none")}</SelectItem>
              <SelectItem value="recommend">{t("policy_recommend")}</SelectItem>
              <SelectItem value="enforce">{t("policy_enforce")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} data-testid="canteen-save-settings">{t("save")}</Button>
      </Card>
    </div>
  );
}

function SchoolSelector({ value, onChange, schools }) {
  const { t } = useI18n();
  if (schools.length <= 1) return null;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-64"><SelectValue placeholder={t("school")} /></SelectTrigger>
      <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name_ar || s.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Stat({ icon: Icon, label, value, color = "primary" }) {
  const colors = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-500/10 text-emerald-600",
    blue: "bg-blue-500/10 text-blue-600",
  };
  return (
    <Card className="p-4 border shadow-none flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </div>
    </Card>
  );
}
